import React, { useCallback } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams
} from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { Toaster } from 'sonner';

import { IntakeProvider } from './context/IntakeContext';
import { STEPS, stepIndexFromParam } from './steps';
import IntakeShell from './components/IntakeShell';
import ContactStep from './pages/ContactStep';
import VehicleStep from './pages/VehicleStep';
import DamageStep from './pages/DamageStep';
import WorkshopStep from './pages/WorkshopStep';
import PhotosStep from './pages/PhotosStep';
import ReviewStep from './pages/ReviewStep';
import DoneStep from './pages/DoneStep';
import ResumeRoute from './pages/ResumeRoute';
import OrganisationPage from './pages/OrganisationPage';
import NotFoundPage from './pages/NotFoundPage';

const STEP_ELEMENTS = {
  contact: <ContactStep />,
  vehicle: <VehicleStep />,
  damage: <DamageStep />,
  workshop: <WorkshopStep />,
  photos: <PhotosStep />,
  review: <ReviewStep />
};

/** Renders the step named by the `:step` number, or bounces to the first. */
const StepRoute = () => {
  const { step } = useParams();
  const index = stepIndexFromParam(step);

  if (index === -1) return <Navigate to=".." replace />;
  return STEP_ELEMENTS[STEPS[index].key];
};

const IntakeRoutes = () => {
  const { teamId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // A resume link hands the token over in router state so it never lingers in
  // the address bar (it is also written to storage for the next visit).
  const resumeToken = location.state?.resumeToken;
  const urlQuoteRequestId = searchParams.get('q') || null;

  /**
   * Put the request id in the address bar as soon as there is one, so the
   * customer's own link identifies their submission rather than depending on
   * this browser's storage.
   */
  const handleQuoteRequestId = useCallback((quoteRequestId) => {
    if (!quoteRequestId) return;

    const params = new URLSearchParams(window.location.search);
    if (params.get('q') === quoteRequestId) return;

    params.set('q', quoteRequestId);
    navigate(`${window.location.pathname}?${params.toString()}`, { replace: true });
  }, [navigate]);

  return (
    <IntakeProvider
      teamId={teamId}
      resumeToken={resumeToken}
      urlQuoteRequestId={urlQuoteRequestId}
      onQuoteRequestId={handleQuoteRequestId}
    >
      <IntakeShell>
        <Routes>
          <Route index element={<Navigate to="1" replace />} />
          <Route path=":step" element={<StepRoute />} />
          <Route path="done" element={<DoneStep />} />
          <Route path="*" element={<Navigate to="1" replace />} />
        </Routes>
      </IntakeShell>
    </IntakeProvider>
  );
};

const App = () => (
  <Sentry.ErrorBoundary
    fallback={
      <div className="min-h-dvh flex items-center justify-center p-6 text-center">
        <p className="text-muted-foreground">
          Something went wrong. Please reload the page — anything you had already entered is saved.
        </p>
      </div>
    }
  >
    <Router>
      <Routes>
        <Route path="/r/:token" element={<ResumeRoute />} />
        <Route path="/:teamId/form/*" element={<IntakeRoutes />} />
        <Route path="/:teamId" element={<OrganisationPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Router>
    <Toaster position="top-center" richColors closeButton />
  </Sentry.ErrorBoundary>
);

export default App;
